// Trust tiers (SCR-17, ARCH §27.12, E-48). PURE (ENG-1).
//
// A script arrives from one of three places, and where it came from decides how much of
// its host slice it may use and how much CPU it gets:
//
//   | Tier         | Source                                     | Verb slice                                  | Budget                     |
//   |--------------|--------------------------------------------|---------------------------------------------|----------------------------|
//   | `local`      | authored in this world by an op            | the full slice for its host kind            | normal                     |
//   | `collection` | a server-selected collection               | the capability set the owner APPROVED       | normal                     |
//   | `foreign`    | imported by a non-op, or not allowlisted   | no `world.write`, no `entity.spawn`         | `SCR.TIER.FOREIGN_OP_BUDGET` |
//
// **The machinery ships; the gate stays shut** (D-TF-98): `SCR.TIER.FOREIGN_ENABLED` is
// false, so a foreign script is refused outright and only ops author or install in the
// shipped default. Tiers make relaxing that POSSIBLE and LEGIBLE; they do not perform it.
//
// Every check here is against the COMPUTED capability set (§27.11), never the declared
// one — a manifest can overstate its powers but not understate them, so the computed set
// is the only one worth gating on or showing a server owner. And every refusal NAMES that
// computed set (E-48), because "refused" with no reason is exactly the answer an owner
// cannot act on.
//
// Nothing here throws, and nothing reads a clock, a policy global or the world: the
// tier, the approved set and the policy all arrive as arguments, so the same inputs
// decide the same outcome on a client, a server and a test.
import { SCR } from '../config';
import { ScriptError } from './lang/lex';
import type { Capability } from './capabilities';

/** §27.12's three tiers. */
export type ScriptTier = 'local' | 'collection' | 'foreign';

/** How far one install is trusted: the tier, plus — for `collection` — the capability
 *  set the server owner approved when they selected the collection, and — for `foreign` —
 *  optionally WHY it is foreign (an un-allowlisted or mismatched collection hash, M47.2),
 *  which leads the E-48 refusal so an owner reads the cause before the consequence. */
export type ScriptTrust =
  | { readonly tier: 'local' }
  | { readonly tier: 'collection'; readonly approved: ReadonlySet<Capability> }
  | { readonly tier: 'foreign'; readonly reason?: string };

/** The tier every install ran at before M47.1, and still the default: an op authoring in
 *  their own world. Frozen and shared — a trust is a value, and nothing mutates one. */
export const LOCAL_TRUST: ScriptTrust = Object.freeze({ tier: 'local' });

/** The server-wide switches the tiers consult. A value rather than a read of
 *  `SCR.TIER` at the call site so a server can be configured (and a test can exercise
 *  the enabled path) without writing to frozen config. */
export interface TierPolicy {
  /** §27.12's `SCR.TIER.FOREIGN_ENABLED`: may a foreign-tier script install at all? */
  readonly foreignEnabled: boolean;
}

/** The shipped policy — the gate shut (D-TF-98). */
export const SHIPPED_TIER_POLICY: TierPolicy = Object.freeze({ foreignEnabled: SCR.TIER.FOREIGN_ENABLED });

/** What a foreign script may never reach, even with the tier enabled (§27.12): it may
 *  not change the world and it may not add to it. Everything else in its host slice —
 *  reads, chat (rate-limited, §27.8), signals (cascade-bounded, §27.10), a mob's own
 *  `self.*` — stays, because those are bounded elsewhere and a script with none of them
 *  could do nothing at all. */
export const FOREIGN_DENIED: ReadonlySet<Capability> = new Set(['world.write', 'entity.spawn']);

/** A computed set, rendered for a refusal message: sorted (computed sets already iterate
 *  sorted, but an approved set arrives from outside), comma-separated, and `none` for a
 *  script that reaches no host verb at all — so the message never shows an empty list
 *  that reads like a formatting bug. */
export function describeCapabilities(set: ReadonlySet<Capability>): string {
  return set.size === 0 ? 'none' : [...set].sort().join(', ');
}

/** §27.12's gate: may a program whose COMPUTED capability set is `computed` install at
 *  this trust under this policy? Returns the refusal (E-48), or null to admit it.
 *
 *  `local` admits anything its host slice compiled — the slice was already enforced by
 *  the compiler (§27.5), and the tier adds no restriction. The other two gate on the
 *  computed set, reporting the FIRST offending capability in sorted order so the message
 *  is stable across machines and across runs. */
export function checkTier(
  trust: ScriptTrust,
  computed: ReadonlySet<Capability>,
  policy: TierPolicy = SHIPPED_TIER_POLICY,
): ScriptError | null {
  const uses = describeCapabilities(computed);
  switch (trust.tier) {
    case 'local':
      return null;
    case 'collection': {
      const unapproved = [...computed].sort().find((cap) => !trust.approved.has(cap));
      if (unapproved === undefined) return null;
      return new ScriptError(
        `capability '${unapproved}' was not approved for this collection — this script uses: ${uses}; approved: ${describeCapabilities(trust.approved)}`,
      );
    }
    case 'foreign': {
      const why = trust.reason ? `${trust.reason} — ` : '';
      if (!policy.foreignEnabled) {
        return new ScriptError(`${why}foreign-tier scripts are disabled on this server — this script uses: ${uses}`);
      }
      const denied = [...computed].sort().find((cap) => FOREIGN_DENIED.has(cap));
      if (denied === undefined) return null;
      return new ScriptError(`${why}a foreign-tier script may not use '${denied}' — this script uses: ${uses}`);
    }
  }
}

/** Two trusts are the same install condition: same tier and, for `collection`, the same
 *  approved set by CONTENTS (the owner re-approving an identical set is not a change).
 *  `install`'s idempotence rests on this — a trust change must recompile, because it can
 *  turn an admitted script into a refused one. */
export function sameTrust(a: ScriptTrust, b: ScriptTrust): boolean {
  if (a.tier !== b.tier) return false;
  if (a.tier === 'foreign' && b.tier === 'foreign') return a.reason === b.reason;
  if (a.tier !== 'collection' || b.tier !== 'collection') return true;
  if (a.approved.size !== b.approved.size) return false;
  for (const cap of a.approved) if (!b.approved.has(cap)) return false;
  return true;
}
